import type { Scene } from "./types";

export const SCENES: Scene[] = [
  {
    id: "lead_intake",
    title: "Lead Intake",
    subtitle: "Where every opportunity starts",
    narration: "This is where every opportunity starts. Clean data in = clean operations downstream.",
    route: "/pipeline",
    whatHappens: "A new lead enters the system. The Lead Integrity Engine validates data quality, checks for duplicates, scores the lead, and routes it to the correct pipeline stage.",
    whyItMatters: "Bad data corrupts everything downstream — scheduling, materials, payments. The system enforces quality at the gate.",
    businessImpact: "Eliminates wasted sales appointments on unqualified leads. Reduces duplicate entries by 90%. Every lead that enters the pipeline is verified and scored.",
    interactionHint: "Review the pipeline view. Notice how leads are scored and routed automatically.",
  },
  {
    id: "sales_conversion",
    title: "Sales Conversion",
    subtitle: "Where revenue is created",
    narration: "This is where revenue is created. Everything downstream depends on this being structured correctly.",
    route: "/pipeline",
    whatHappens: "A lead progresses through the opportunity lifecycle: appointment scheduled, consultation completed, proposal built, contract signed. Every transition is gated and audited.",
    whyItMatters: "Unstructured sales processes leak revenue. The system enforces data capture at every stage so nothing is left to memory.",
    businessImpact: "Conversion rates increase when every follow-up is tracked. No opportunity is forgotten. Revenue attribution is precise.",
    interactionHint: "Open an opportunity and review the lifecycle stages and required fields.",
  },
  {
    id: "build_review",
    title: "Build Review",
    subtitle: "Quality gate before execution",
    narration: "Before execution, the job must be correct. This prevents costly mistakes later.",
    route: "/jobs",
    whatHappens: "The Build Review workspace validates scope, materials, measurements, and photos. The system checks for missing or incompatible items before approving the job for ordering.",
    whyItMatters: "Errors caught here cost minutes. Errors caught on-site cost thousands. This is the cheapest place to fix a mistake.",
    businessImpact: "Reduces material re-orders by 60%. Eliminates scope disputes. Every job that enters production has been verified.",
    interactionHint: "Review the job details and notice the validation checks.",
  },
  {
    id: "job_creation",
    title: "Command Center",
    subtitle: "The operator's cockpit",
    narration: "This is now a live job. Everything flows from here — every task, every dollar, every decision.",
    route: "/",
    whatHappens: "The Command Center aggregates every active job, surfacing what needs attention right now. Suggested actions are ranked by urgency and financial impact.",
    whyItMatters: "Operators never have to guess what to do next. The system tells them.",
    businessImpact: "Reduces morning planning from 30 minutes to 30 seconds. No job falls through the cracks.",
    interactionHint: "Explore the suggested actions and KPI cards.",
  },
  {
    id: "payment_structure",
    title: "Payment Structure",
    subtitle: "Deal structure prevents downstream failures",
    narration: "We structure the deal upfront so nothing breaks later — cash, financing, or card.",
    route: "/accounting",
    whatHappens: "The financial engine assigns payment plans based on deal type. Cash and card deals require deposits. Financing deals require lender approval. Every obligation has a timing gate.",
    whyItMatters: "Unstructured payment collection is the #1 cause of cash flow problems in contracting. The system eliminates ambiguity.",
    businessImpact: "Cash position is always accurate. Every job has a defined payment structure before work begins.",
    interactionHint: "Review the financial dashboard and notice how payment plans are structured.",
  },
  {
    id: "scheduling_blocked",
    title: "Scheduling Gate",
    subtitle: "Deposit missing — system enforces payment first",
    narration: "This is where most companies lose money. Try to schedule without a deposit — the system won't allow it.",
    route: "/scheduling",
    whatHappens: "The scheduling engine checks financial clearance before allowing crew assignment. Jobs without deposits are gated automatically. Once payment is collected, scheduling unlocks instantly.",
    whyItMatters: "Prevents unpaid installations — the #1 cash flow killer for contractors.",
    businessImpact: "Eliminates $0-collected installs. Every scheduled job has money committed. Cash conversion improves by 2–3 weeks.",
    interactionHint: "Attempt to schedule a job and observe the financial gate in action.",
  },
  {
    id: "fulfillment_prep",
    title: "Fulfillment & Prep",
    subtitle: "Logistics make or break execution",
    narration: "Execution only works if logistics are handled correctly — materials, permits, warehouse staging.",
    route: "/ordering",
    whatHappens: "The ordering queue processes material requirements. Permits are tracked through approval stages. The warehouse receives, verifies, and stages materials for specific jobs.",
    whyItMatters: "A truck roll without materials is a wasted day. The system ensures every prerequisite is confirmed before dispatch.",
    businessImpact: "Reduces failed truck rolls by 90%. Material waste drops. Every site visit is productive.",
    interactionHint: "Review the ordering queue and material readiness indicators.",
  },
  {
    id: "dispatch_execution",
    title: "Dispatch & Execution",
    subtitle: "Planning turns into action",
    narration: "This is where planning turns into execution. The crew is dispatched with full confidence.",
    route: "/dispatch-intelligence",
    whatHappens: "Dispatch intelligence shows crew assignments, drive times, and job priority. The system has already verified materials are staged, permits are cleared, and payments are collected.",
    whyItMatters: "No surprises on install day. Every prerequisite was checked before the truck rolled.",
    businessImpact: "Crew utilization increases. Drive time decreases. Every dispatch is backed by verified readiness.",
    interactionHint: "Review the dispatch queue and job readiness indicators.",
  },
  {
    id: "install_start",
    title: "Install Day",
    subtitle: "The job is live in the field",
    narration: "The job is now live in the field. Real-time tracking begins.",
    route: "/installer",
    whatHappens: "The installer dashboard shows today's assignments with material checklists, scope of work, and pre-install verification. The system tracks progress in real time.",
    whyItMatters: "Field teams need clarity, not confusion. Every piece of information is at their fingertips.",
    businessImpact: "Installation time decreases. Errors reduce. Customer satisfaction increases.",
    interactionHint: "Review the installer dashboard and job workflow.",
  },
  {
    id: "installer_wrap",
    title: "Installer Wrap",
    subtitle: "End-of-day accountability — nothing gets skipped",
    narration: "The installer must complete a structured wrap before leaving. No shortcuts. No exceptions.",
    route: "/jobs",
    whatHappens: "The wrap captures completion status, payment collection, customer communication, and a service prevention checklist. The system blocks job exit if anything is missing.",
    whyItMatters: "Every job has a documented outcome. Ambiguity is eliminated at the source.",
    businessImpact: "Reduces service callbacks by 40%. Payment collection rate increases to 95%+. No job is left in limbo.",
    interactionHint: "Open a job and review the wrap enforcement flow.",
  },
  {
    id: "qa_issues",
    title: "QA & Issue Management",
    subtitle: "Quality gate catches problems before customers do",
    narration: "QA inspection finds issues before the customer does. The system creates tracked issues and blocks premature closeout.",
    route: "/issues",
    whatHappens: "The QA engine logs failures, creates issue records, notifies the ops team, and prevents premature closeout. Jobs stay active until resolution. Service tickets are tracked separately.",
    whyItMatters: "Quality problems caught here cost a fraction of what they cost after handoff. Service calls are prevented, not reacted to.",
    businessImpact: "Warranty costs drop. Customer satisfaction scores rise. Reputation is protected.",
    interactionHint: "Review the issues list and notice the linked job states.",
  },
  {
    id: "payment_resolution",
    title: "Payment Resolution",
    subtitle: "No balance left behind",
    narration: "The system enforces final payment before closeout. No exceptions without admin override and audit trail.",
    route: "/accounting",
    whatHappens: "The financial engine calculates remaining balance, generates the final invoice, and gates closeout until payment is resolved — collected, financed, or explicitly deferred.",
    whyItMatters: "Revenue leakage is the silent killer of contractor businesses. This eliminates it.",
    businessImpact: "Accounts receivable shrinks. Cash conversion cycle improves. Every dollar is accounted for.",
    interactionHint: "Review outstanding balances and the closeout gate.",
  },
  {
    id: "closeout",
    title: "Project Closeout",
    subtitle: "The job is officially complete",
    narration: "The job is officially complete — clean, verified, and fully accounted for.",
    route: "/closeout",
    whatHappens: "The closeout gate verifies: QA passed, payments resolved, wrap submitted, no open issues. Only then can the job be marked complete. Every closeout is auditable.",
    whyItMatters: "Premature closeout hides problems. The system ensures every job meets the standard before it's closed.",
    businessImpact: "Zero jobs closed with unresolved issues. Zero jobs closed with outstanding balances. Complete audit trail.",
    interactionHint: "Review the closeout requirements and decision cockpit.",
  },
  {
    id: "customer_portal",
    title: "Customer Portal",
    subtitle: "Transparency builds trust",
    narration: "This is what your customer sees — clarity, trust, and transparency without calling the office.",
    route: "/portals",
    whatHappens: "A white-labeled portal gives customers real-time visibility into their project. Timeline events, documents, photos, and payment status are all accessible.",
    whyItMatters: "Reduces inbound calls by 60%. Customers feel informed and confident. Trust drives referrals.",
    businessImpact: "Office staff spends less time on status calls. Customer referral rates increase. Brand perception improves.",
    interactionHint: "Explore the portal hub and preview a customer view.",
  },
  {
    id: "automation_followup",
    title: "Automation & Follow-Up",
    subtitle: "The system never forgets",
    narration: "Automations, follow-ups, and alerts run continuously. Nothing falls through the cracks.",
    route: "/automations",
    whatHappens: "The automation engine monitors every job for state changes and triggers appropriate actions — reminders, escalations, notifications, follow-ups. Every execution is logged and auditable.",
    whyItMatters: "Human memory is unreliable. The system ensures nothing slips through, 24/7.",
    businessImpact: "Operations run even when the office is closed. Response times drop from hours to minutes. Follow-up compliance reaches 100%.",
    interactionHint: "Review the automation rules and execution history.",
  },
  {
    id: "reporting_intelligence",
    title: "Reporting & Intelligence",
    subtitle: "Where decisions are made",
    narration: "This is where data becomes decisions — revenue, risk, performance, and opportunity.",
    route: "/intelligence",
    whatHappens: "Dashboards surface revenue leakage, operational risk, sales performance, lead source ROI, and override intelligence. Every metric supports drill-down to the underlying record.",
    whyItMatters: "You can't improve what you can't measure. The system makes every metric actionable.",
    businessImpact: "Leadership sees the full picture. Decisions are data-driven. Problems are caught before they compound.",
    interactionHint: "Explore the intelligence tabs and drill into specific metrics.",
  },
  {
    id: "system_control",
    title: "System Control",
    subtitle: "How the system is governed",
    narration: "This is how Koredor is governed, configured, and continuously improved.",
    route: "/control-plane",
    whatHappens: "The Control Plane manages workflow rules, safety controls, validation gates, and system settings. The Friction Log captures operator pain points for continuous improvement.",
    whyItMatters: "A system that can't be tuned becomes a liability. Koredor is fully configurable without code changes.",
    businessImpact: "Operational rules adapt to the business, not the other way around. Continuous improvement is built in.",
    interactionHint: "Review the control plane settings and friction log.",
  },
];
